import React, { FC, useMemo } from 'react'
import { useUser } from '@clerk/clerk-react'
import './pledge-list.css'

// COMPONENTS
import Divider from '@components/atoms/divider'
import PledgeCell from '@components/molecules/pledge-cell'

// UTILITIES
import { isNonEmptyString } from '@utils/type-checkers'
import { getGroupStatus } from '@utils/temp-helpers'
import { flatten } from '@utils/string-helper'
import { useGroupSettings } from '../../../routes/group/_localStore/_hooks'


interface Props extends React.HTMLAttributes<HTMLDivElement> {
    group: Group
    listType?: 'all' | 'claimed' | 'unclaimed'
    lockedSlots?: Record<string, LockingItem>
    nonParticipant?: boolean
    showParticipantLabel?: boolean
    showUnclaimedParticipants?: boolean
    showOutline?: boolean
}

const PledgeList: FC<Props> = (props) => {
    const { className: classProps, group, listType = 'all', nonParticipant = false, showUnclaimedParticipants = true, showParticipantLabel = true, showOutline = false, lockedSlots = {}, ...divProps } = props
    const { isSignedIn, user } = useUser()
    const className = flatten('pledge-list', classProps)
    const { pledges = [] } = group

    const { groupStatus, isLocked } = getGroupStatus(group)

    const { state } = useGroupSettings()
    const { pledgeToRemove } = state

    const { userPledges, claimedPledges, openPledges } = useMemo(() => (pledges || []).reduce<{ userPledges: Pledge[], claimedPledges: Pledge[], openPledges: Pledge[] }>((prev, pledge) => {
        if (isSignedIn && isNonEmptyString(user.id) && pledge.user?.id === user.id) return { ...prev, userPledges: [...prev.userPledges, pledge] }
        else if (!!pledge.transactionId || !!pledge.user?.id) return { ...prev, claimedPledges: [...prev.claimedPledges, pledge] }
        else return { ...prev, openPledges: [...prev.openPledges, pledge] }
    }, { userPledges: [], claimedPledges: [], openPledges: [] }), [isSignedIn, user?.id, pledges])

    return (
        <div className={className} {...divProps}>
            {!nonParticipant && <div>
                {(groupStatus === 'active' && showParticipantLabel && showUnclaimedParticipants && listType === 'unclaimed') &&
                    <p className="text-body" style={{ marginBottom: openPledges.length > 0 ? '1rem' : '', textAlign: 'center' }}>
                        {(((user?.id === group.organizer?.id) && openPledges.length > 0) || (user?.id !== group.organizer?.id))
                            ? <strong>Participant(s) that have not joined yet</strong>
                            : (claimedPledges.length < 1) && <strong className='text-gray-500'>No participant(s) have joined yet</strong>
                        }
                    </p>
                }
                {listType === 'unclaimed'
                    ? (groupStatus === 'canceled') ? null : openPledges.map((pledge, index) => (showUnclaimedParticipants && (pledge.id !== pledgeToRemove)) ? (
                        <PledgeCell
                            key={`${pledge.user?.id}-${index}`}
                            group={group}
                            pledge={pledge}
                            status={lockedSlots[`${pledge.id}`]}
                            showStatusFlavor={!isLocked}
                            verified={(group.organizer?.id === pledge.user?.id)}
                            style={{ color: 'var(--splitcard-gray-500)', marginTop: index === 0 ? undefined : '1rem' }}
                        >
                        </PledgeCell>
                    ) : null)
                    : [...claimedPledges, ...(listType !== 'claimed' ? openPledges : [])].map((pledge, index) => ((!!pledge.transactionId || !!pledge.user?.id)) ? (
                        <PledgeCell
                            key={`${pledge.user?.id}-${index}`}
                            group={group}
                            pledge={pledge}
                            status={lockedSlots[`${pledge.id}`]}
                            showStatusFlavor={!isLocked}
                            showOutline={showOutline}
                            verified={(group.organizer?.id === pledge.user?.id)}
                            style={{ marginTop: index === 0 ? undefined : '1rem' }}
                        >
                            {(group.organizer?.id === pledge.user?.id) && <p className='text-body-2'>Organizer</p>}
                        </PledgeCell>
                    ) : null)
                }
            </div>}
            {nonParticipant && <>
                {claimedPledges
                    .filter(pledge => pledge.user?.id !== user?.id)
                    .map((pledge, index) => (
                        <PledgeCell
                            key={`${pledge.user?.id}-${index}`}
                            group={group}
                            pledge={pledge}
                            status={lockedSlots[`${pledge.id}`]}
                            editable={(!isLocked && groupStatus === 'active')}
                            showStatusFlavor={!isLocked}
                            verified={(group.organizer?.id === pledge.user?.id)}
                            nonParticipant={nonParticipant}
                            showOutline={showOutline}
                            style={{ marginTop: index === 0 ? undefined : '1rem' }}
                        >
                            {(group.organizer?.id === pledge.user?.id) && <p className='text-body-2'>Organizer</p>}
                        </PledgeCell>
                    ))}
                {claimedPledges.length > 0 && openPledges.length > 0 && groupStatus !== 'canceled' && !isLocked && showUnclaimedParticipants && <div>
                    <Divider margin={true} />
                </div>}
            </>}
        </div>
    )
}

export default PledgeList
